import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Dialog } from 'primereact/dialog'
import { Stepper, StepperRefAttributes } from 'primereact/stepper'
import { StepperPanel } from 'primereact/stepperpanel'

import {
  HandleableError,
  getHandleableError,
} from '../../../../../../../utils/errors/isXiaError'
import { getCultureFromLanguage } from '../../../../../../../utils/helper/getCultureFromLanguage'
import { isEmpty } from '../../../../../../../utils/helper/isEmpty'
import {
  MasterDataSupplier,
  requestPrepareUploadableSuppliers,
  requestUploadSuppliers,
} from '../../../data'
import { SummaryPanel, UploadPanel, VerifyPanel } from './StepperPanels'
import { UploadPanelFormValues } from './common/UploadPanelFormValues'

type MasterDataDialogProps = {
  visible: boolean
  onHide: () => void
}

export const MasterDataUploadDialog: React.FC<MasterDataDialogProps> = ({
  onHide,
  visible,
}) => {
  const { t, i18n } = useTranslation()
  const stepperRef = useRef<StepperRefAttributes | null>(null)
  const [deleteExistingSuppliers, setDeleteExistingSuppliers] =
    useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<HandleableError | null>(null)
  const [data, setData] = useState<MasterDataSupplier[] | null>(null)

  const uploadPanelFormik = useFormik<UploadPanelFormValues>({
    initialValues: {
      suppliers: undefined,
      companyCodesRefs: undefined,
      deleteExistingSuppliers: true,
    },
    validate: (data: UploadPanelFormValues) => {
      let errors: FormikErrors<UploadPanelFormValues> = {}

      if (isEmpty(data.suppliers))
        errors.suppliers = t(
          'masterDataManagement.suppliers.table.upload.dialog.errors.required',
          {
            fieldName: t(
              'masterDataManagement.suppliers.table.upload.dialog.uploadPanel.fields.csv',
            ),
          },
        )

      if (isEmpty(data.companyCodesRefs))
        errors.companyCodesRefs = t(
          'masterDataManagement.suppliers.table.upload.dialog.errors.required',
          {
            fieldName: t(
              'masterDataManagement.suppliers.table.upload.dialog.uploadPanel.fields.csv',
            ),
          },
        )

      return errors
    },
    onSubmit: async (data: UploadPanelFormValues) => {
      onUploadCsv(data)
    },
  })

  const uploadCsv = async (suppliersCsv: File, companyCodesRefsCsv: File) => {
    try {
      setIsLoading(true)
      const culture = getCultureFromLanguage(i18n.language)
      const result = await requestPrepareUploadableSuppliers(
        suppliersCsv,
        companyCodesRefsCsv,
        culture,
      )
      setData(result)
    } catch (error) {
      const handleableError = getHandleableError(error)
      setError(handleableError)
    } finally {
      setIsLoading(false)
    }
  }

  const uploadData = async () => {
    try {
      setIsLoading(true)
      await requestUploadSuppliers(data!, deleteExistingSuppliers)
    } catch (error) {
      const handleableError = getHandleableError(error)
      setError(handleableError)
    } finally {
      setIsLoading(false)
    }
  }

  const onUploadCsv = (data: UploadPanelFormValues) => {
    uploadCsv(data.suppliers!, data.companyCodesRefs!)
    stepperRef.current?.nextCallback()
  }

  const onVerifyNext = () => {
    uploadData()
    stepperRef.current?.nextCallback()
  }

  const onVerifyPrev = () => {
    setError(null)
    setData(null)
    uploadPanelFormik.resetForm()
    stepperRef.current?.prevCallback()
  }

  const onExit = () => {
    uploadPanelFormik.resetForm()
    setIsLoading(false)
    setError(null)
    setData(null)
    setDeleteExistingSuppliers(false)
    onHide()
  }

  const header = (
    <>{t('masterDataManagement.suppliers.table.upload.dialog.title')}</>
  )
  const footer = <></>

  return (
    <Dialog
      onHide={onExit}
      visible={visible}
      header={header}
      footer={footer}
      style={{ width: '75rem' }}
    >
      <Stepper ref={stepperRef} linear>
        <StepperPanel
          header={t(
            'masterDataManagement.suppliers.table.upload.dialog.uploadPanel.title',
          )}
        >
          <UploadPanel formik={uploadPanelFormik} />
        </StepperPanel>
        <StepperPanel
          header={t(
            'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.title',
          )}
        >
          <VerifyPanel
            data={data || undefined}
            error={error || undefined}
            deleteExistingSuppliers={deleteExistingSuppliers}
            setDeleteExistingSuppliers={setDeleteExistingSuppliers}
            onNext={onVerifyNext}
            onPrev={onVerifyPrev}
            isLoading={isLoading}
          />
        </StepperPanel>
        <StepperPanel
          header={t(
            'masterDataManagement.suppliers.table.upload.dialog.summaryPanel.title',
          )}
        >
          <SummaryPanel
            onExit={onExit}
            isLoading={isLoading}
            error={error || undefined}
            data={data || undefined}
          />
        </StepperPanel>
      </Stepper>
    </Dialog>
  )
}
